import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Loader2, MailWarning, Sparkles } from "lucide-react";

const CAP_LABELS = {
  max_active_roles: "Active roles",
  max_clients: "Clients",
  team_seats_limit: "Team seats",
  talent_reach_level: "Talent Reach",
  can_use_pre_screen: "AI pre-screening",
  advanced_matching: "Advanced matching",
  analytics_enabled: "Analytics",
  can_use_candidate_pools: "Candidate pools",
  can_use_api: "API access",
  can_use_white_label: "White-label",
  criteria_profiles_limit: "Criteria profiles",
  advanced_filters: "Advanced filters",
  alerts_enabled: "Email alerts",
};

function capValue(v) {
  if (v === -1) return "Unlimited";
  if (v === true) return "Yes";
  if (v === false) return null;
  return String(v);
}

export default function Plans() {
  const { user, checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [resending, setResending] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/billing/plans");
      setData(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const choose = async (planId, isPilot) => {
    setBusy(planId);
    try {
      const { data: res } = await api.post("/billing/select-plan", { plan: planId });
      if (res.pilot) {
        toast.success(res.message || "Request recorded.");
      } else {
        toast.success(`You're on ${planId.replace(/_/g, " ")} — 14-day trial active.`);
      }
      await checkAuth();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Confirmation email sent.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResending(false);
    }
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="plans-loading">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="plans-page">
      <div className="mb-2 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-slate-900" />
        <h1 className="text-2xl font-extrabold text-slate-900">Choose your plan</h1>
      </div>
      <p className="mb-6 text-sm text-slate-500">
        Every plan starts with a {data.trial_days}-day trial. Eligibility accuracy is never gated — plans differ only in volume and tools.
      </p>

      {user && user.role !== "candidate" && !user.email_verified && (
        <div className="mb-6 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
          data-testid="email-verify-banner">
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <MailWarning className="h-4 w-4" />
            Confirm your email to fully activate your account.
          </div>
          <Button size="sm" variant="outline" onClick={resend} disabled={resending} data-testid="resend-verify-btn">
            {resending ? "Sending…" : "Resend email"}
          </Button>
        </div>
      )}

      <div className="grid gap-5 md:grid-cols-3">
        {data.plans.map((p) => {
          const current = p.id === data.current_plan;
          const caps = Object.entries(p.capabilities)
            .map(([k, v]) => [CAP_LABELS[k] || k, capValue(v)])
            .filter(([, v]) => v !== null);
          return (
            <div key={p.id}
              className={`flex flex-col rounded-xl border p-5 ${current ? "border-slate-900 ring-2 ring-slate-900/10" : "border-slate-200"}`}
              data-testid={`plan-card-${p.id}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-900">{p.label}</h3>
                {current && (
                  <span className="rounded-full bg-slate-900 px-2 py-0.5 text-xs font-semibold text-white" data-testid={`plan-current-${p.id}`}>
                    Current
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">{p.pilot ? "By request" : "Self-serve"}</p>
              <ul className="mt-4 flex-1 space-y-2">
                {caps.map(([label, val]) => (
                  <li key={label} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    <span>{label}: <strong className="text-slate-800">{val}</strong></span>
                  </li>
                ))}
              </ul>
              <Button
                className="mt-5 w-full bg-slate-900 hover:bg-slate-800"
                disabled={current || busy === p.id}
                onClick={() => choose(p.id, p.pilot)}
                data-testid={`plan-select-${p.id}`}>
                {busy === p.id ? "Working…" : current ? "Active" : p.pilot ? "Request this plan" : "Start trial"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
